import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { SplashScreen } from '@capacitor/splash-screen'
import { App as CapApp } from '@capacitor/app'
import { PushNotifications } from '@capacitor/push-notifications'
import { ScreenOrientation } from '@capacitor/screen-orientation'
import { isTabletDevice } from '@/utils/deviceUtils'
import router from '@/router'

export const isNative = Capacitor.isNativePlatform()

export async function setStatusBarStyle(isDark: boolean) {
  if (!isNative) return
  await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light })
}

export async function initCapacitor() {
  if (!isNative) return

  const platform = Capacitor.getPlatform()
  const isIOS = platform === 'ios'
  const shouldLockToPortrait = isIOS && !isTabletDevice()

  document.body.classList.add('native-app')

  // Keep app portrait by default. Specific views (like video players)
  // can temporarily unlock orientation when needed.
  if (shouldLockToPortrait) {
    await ScreenOrientation.lock({ orientation: 'portrait' }).catch(() => {})
  }

  // Status bar style is synced with theme from App.vue via setStatusBarStyle()
  if (platform === 'android') {
    await StatusBar.setBackgroundColor({ color: '#0EA5E9' })
  }

  // Hide splash after app is ready
  await SplashScreen.hide()

  // Handle hardware back button (Android)
  CapApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      router.back()
    } else {
      CapApp.minimizeApp()
    }
  })

  // Handle deep links (Universal Links iOS / App Links Android / custom scheme)
  const navigateFromUrl = (url: string) => {
    try {
      const parsed = new URL(url)
      // Pro custom schemes (např. teamapp://path) je pathname občas prázdné a path je v host
      let target = `${parsed.pathname}${parsed.search}${parsed.hash}`
      if (!parsed.pathname || parsed.pathname === '/') {
        if (parsed.host && parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
          target = `/${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`
        }
      }
      if (!target || target === '/') return
      console.log('[deeplink] navigate to', target, 'from', url)
      router.isReady().then(() => router.replace(target).catch((e) => console.warn('[deeplink] push failed', e)))
    } catch (e) {
      console.warn('[deeplink] parse failed', url, e)
    }
  }

  CapApp.addListener('appUrlOpen', ({ url }) => {
    console.log('[deeplink] appUrlOpen', url)
    navigateFromUrl(url)
  })

  // Cold-start: pokud byla appka spuštěna z deep linku, listener nemusí stihnout přijít.
  CapApp.getLaunchUrl()
    .then((res) => {
      if (res?.url) {
        console.log('[deeplink] launchUrl', res.url)
        navigateFromUrl(res.url)
      }
    })
    .catch(() => {})

  // Clear badge + delivered notifications when app comes to foreground
  // (only after push registration has happened — otherwise iOS throws an error)
  CapApp.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      PushNotifications.removeAllDeliveredNotifications().catch(() => {})
    }
  })

  // Handle push notification tap → navigate to route + clear badge
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    PushNotifications.removeAllDeliveredNotifications().catch(() => {})
    const data = action.notification.data
    if (data?.route) {
      router.isReady().then(() => router.push(data.route))
    }
  })
}
